import { useEffect, useState } from "react";

// material-ui

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";

// project imports
import CircularWithPath from "components/@extended/progress/CircularWithPath";

import { openSnackbar } from "api/snackbar";

// assets

// types
import { SnackbarProps } from "types/snackbar";
import { CustomerList } from "types/customer";
import { useAppDispatch } from "hooks";
import { ParentCat } from "model/categories";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableModal from "../../../../../components/ReusableComponent/ReusableModal";
import { useQueryClient } from "@tanstack/react-query";

// constant
const getInitialValues = (customer: CustomerList | null) => {
  if (customer) {
    return {
      name: customer.name || "",
      name_trans: customer.name_trans.ar || "",
      is_active: customer.is_active || false,
      order: customer.order || 0,
    };
  } else {
    return {
      parent_id: "",
      name: "",
      order: 0,
      is_active: false,
      name_trans: "",
    };
  }
};
// ==============================|| CATEGORY ADD / EDIT - FORM ||============================== //

const FormCustomerAdd = ({
  open,
  customer,
  id,
  loading,
  fetchData,
  setLoading,
  handleClose,
  setResult,
}: {
  open: boolean;
  loading: any;
  id: number;
  setLoading: any;
  fetchData: any;
  customer: any | null;
  handleClose: () => void;
  setResult: any;
}) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const [categories, setCategories] = useState<ParentCat[]>([]);
  const [categoriesDetails, setcategoriesDetails] = useState<any>();
  const [selectedParentCat, setSelectedParentCat] = useState<ParentCat | null>(
    null
  );

  const fetchCatData = async () => {
    try {
      const response = await USERAXIOS.get("/category?page=1");
      const { data } = response.data;
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchOneCategory = async () => {
    if (!customer?.id) return;
    try {
      const response = await USERAXIOS.get(`/category/${customer.id}`);
      const { data } = response.data;
      setcategoriesDetails(data);
      setSelectedParentCat(data.parent);
    } catch (error) {
      console.error("Error fetching category details:", error);
    }
  };

  useEffect(() => {
    fetchCatData();
    fetchOneCategory();
  }, []);

  useEffect(() => {
    setLoading(false);

    // Initialize selectedParentCountryId and selectedParentCountry if customer exists
    if (customer && customer.parent_name) {
      const selectedCat = categories.find(
        (country) => country.name === customer.parent_id
      );
      if (selectedCat) {
        setSelectedParentCat(selectedCat);
        setSelectedParentCategoryId(selectedCat.name);
      }
    }
  }, [id, customer, categories]);

  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState<
    string | null
  >(null);
  const queryClient = useQueryClient();

  const CustomerSchema = Yup.object().shape({
    name: Yup.string().required("English name is required"),
    name_trans: Yup.string().required("Arabic name is required"),
    order: Yup.number().required("Order is required"),
  });

  const handleChangeParentName = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const selectedCat = categories.find((cat) => cat.name === selectedValue);
    setSelectedParentCategoryId(event.target.value);
    if (selectedCat) {
      setSelectedParentCat(selectedCat);
    } else {
      setSelectedParentCat({ id: 0, name: "No parent" });
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(customer!),
    validationSchema: CustomerSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const categoryData = {
          parent_id: selectedParentCat ? selectedParentCat.id : null,
          name: values.name,
          order: values.order,
          is_active: values.is_active,
          name_trans: {
            ar: values.name_trans,
          },
        };

        if (customer) {
          const response = await USERAXIOS.post(
            `/category/${customer.id}`,
            categoryData
          );
          const updatedCategory = response.data.data;

          if (updatedCategory) {
            setResult((prev) =>
              prev.map((cat) =>
                cat.id === updatedCategory.id ? updatedCategory : cat
              )
            );

            openSnackbar({
              open: true,
              message: "Category Edited Successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(`/category`, categoryData);

          const newCategory = response.data.data;

          if (newCategory) {
            setResult((prev) => [...prev, newCategory]);

            openSnackbar({
              open: true,
              message: "Category Added Successfully!",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        queryClient.invalidateQueries({ queryKey: ["categories"] });

        setSubmitting(false);
        handleClose();
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );
  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <LocalizationProvider>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle
              color="primary"
              style={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              {customer ? (
                <FormattedMessage id="Edit Category" />
              ) : (
                <FormattedMessage id="New Category" />
              )}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <Grid container>
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-name">
                      <FormattedMessage id="English Name" />{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-name"
                      placeholder={intl.formatMessage({
                        id: "Enter english name",
                        defaultMessage: "Enter english name",
                      })}
                      {...getFieldProps("name")}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Stack>
                </Grid>
                <Grid item md={1} />
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-name">
                      {<FormattedMessage id="Arabic Name" />}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-name"
                      name="name_trans"
                      {...getFieldProps("name_trans")}
                      placeholder={intl.formatMessage({
                        id: "enter-arabic-name",
                        defaultMessage: "Enter arabic name",
                      })}
                      error={Boolean(touched.name_trans && errors.name_trans)}
                      helperText={touched.name_trans && errors.name_trans}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={5} mb={2}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-parent_name">
                      <FormattedMessage id="Parent Name" />
                      <span style={{ color: "grey" }}>{` (Optional)`}</span>
                    </InputLabel>

                    <Select
                      id="parent_name"
                      value={
                        selectedParentCat
                          ? selectedParentCat.name
                          : customer?.parent_name
                      }
                      input={
                        <OutlinedInput
                          id="select-column-hiding"
                          placeholder="Sort by"
                        />
                      }
                      renderValue={(selected) => {
                        if (!selected) {
                          return <Typography variant="subtitle1"></Typography>;
                        }
                        const cat = categories.find(
                          (item) => item.name === selected
                        );
                        return (
                          <Typography variant="subtitle2">
                            {cat ? cat.name : ""}
                          </Typography>
                        );
                      }}
                      onChange={handleChangeParentName}
                    >
                      {/* <MenuItem value="No parent" >
                            No parent
                          </MenuItem> */}
                      {categories.map((column: any) => (
                        <MenuItem key={column.id} value={column.name}>
                          <ListItemText primary={column.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </Grid>
                <Grid md={1} />
                <Grid item xs={12} sm={5}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-order">
                      <FormattedMessage id="Order" />{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      type="number"
                      id="customer-order"
                      placeholder={intl.formatMessage({
                        id: "enter.order",
                        defaultMessage: "Enter order",
                      })}
                      {...getFieldProps("order")}
                      error={Boolean(touched.order && errors.order)}
                      helperText={touched.order && errors.order}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-is_active">
                      <FormattedMessage id="Status" />
                    </InputLabel>
                    <FormControl fullWidth>
                      <FormControlLabel
                        control={
                          <Switch
                            id="customer-is_active"
                            checked={formik.values.is_active}
                            {...getFieldProps("is_active")}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setFieldValue("is_active", checked);
                            }}
                          />
                        }
                        label={
                          formik.values.is_active ? (
                            <FormattedMessage id="Active" />
                          ) : (
                            <FormattedMessage id="Inactive" />
                          )
                        }
                      />
                    </FormControl>
                    {touched.is_active && errors.is_active && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-email-login"
                        sx={{ pl: 1.75 }}
                      >
                        {errors.is_active}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    {customer ? (
                      <Button
                        disabled={isSubmitting}
                        fullWidth
                        type="submit"
                        variant="contained"
                      >
                        <FormattedMessage id="Update" />
                      </Button>
                    ) : (
                      <Button
                        disabled={isSubmitting}
                        fullWidth
                        type="submit"
                        variant="contained"
                      >
                        <FormattedMessage id="Submit" />
                      </Button>
                    )}
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </ReusableModal>
  );
};

export default FormCustomerAdd;
